import type { ApiClient } from '../client';
import {
  mapPoolItemEmbed,
  type IframePoolOrderItem,
  type RawPoolItemEmbed,
} from './poolOrders';

export interface IframeInventoryPage {
  items: IframePoolOrderItem[];
  totalCount: number;
  page: number;
  per: number;
}

export interface IframeInventoryParams {
  page?: number;
  per?: number;
}

/**
 * Paginated opened items owned by the current platform-session user —
 * feeds the "Drops" tab of My Packs. Mirrors shared v1
 * `pool_wallets/inventory` but scoped via the session's user.
 */
export async function getInventory(
  client: ApiClient,
  params: IframeInventoryParams = {},
): Promise<IframeInventoryPage> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.per) qs.set('per', String(params.per));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  interface RawMeta {
    total_count?: number;
    page?: number;
    per?: number;
  }

  const raw = await client.request<{ pool_items: RawPoolItemEmbed[]; meta?: RawMeta }>(
    `/loot_machine/api/game_platform/iframe/v1/inventory${suffix}`,
    { method: 'GET' },
  );

  const items = (raw.pool_items ?? []).map(mapPoolItemEmbed);
  return {
    items,
    totalCount: Number(raw.meta?.total_count ?? items.length),
    page: Number(raw.meta?.page ?? 1),
    per: Number(raw.meta?.per ?? items.length),
  };
}

/**
 * Body shape for {@link sellInventoryItems}. Either we sell every item the
 * backend's user-scope yields (`{ all: true }`) or we pass an explicit list
 * of pool_item ids. Discriminated so callers can't mix both.
 */
export type SellInventoryParams =
  | { poolItemIds: Array<string | number> }
  | { all: true };

/**
 * Outcome of a bulk sell. `sold` mirrors the serialized pool_items the
 * backend reports as sold (parsed via the same {@link mapPoolItemEmbed} the
 * inventory list uses); `failed` lists the ids it couldn't process, each with
 * a human-readable reason.
 */
export interface SellInventoryResult {
  sold: IframePoolOrderItem[];
  failed: Array<{ id: string; message: string }>;
}

interface RawSellInventoryFailure {
  id?: number | string;
  message?: string | null;
}

/**
 * Cross-order buyback for the current platform-session user. Unlike the
 * per-order `sellPoolItems`, this hits the inventory-scoped bulk endpoint so
 * a single call can sell cards drawn from many different pool_orders — the
 * backend scopes by user, not by order. Pass an explicit `pool_item_ids`
 * list (`{ poolItemIds }`) or `{ all: true }` to sell the whole inventory.
 */
export async function sellInventoryItems(
  client: ApiClient,
  params: SellInventoryParams,
): Promise<SellInventoryResult> {
  const body =
    'all' in params
      ? { all: true }
      : { pool_item_ids: params.poolItemIds };

  const raw = await client.request<{
    result?: {
      pool_items?: RawPoolItemEmbed[];
      failed?: RawSellInventoryFailure[];
    };
  }>(`/loot_machine/api/game_platform/iframe/v1/inventory/sell`, {
    method: 'POST',
    body,
  });

  const result = raw.result;
  return {
    sold: (result?.pool_items ?? []).map(mapPoolItemEmbed),
    failed: (result?.failed ?? []).map((f) => ({
      id: String(f.id ?? ''),
      message: typeof f.message === 'string' ? f.message : '',
    })),
  };
}
